import { useCallback, useMemo, useState } from "react";
import { CaretUp, CaretDown, CaretUpDown } from "@phosphor-icons/react";

/**
 * Hook para tablas ordenables por columna con persistencia en sessionStorage.
 *
 * Uso:
 *   const { sorted, sortKey, sortDir, toggle, headerProps } = useSortableTable(
 *       rows,
 *       "storage-key",
 *       {
 *           name: (r) => r.name.toLowerCase(),
 *           amount: (r) => Number(r.amount || 0),
 *       },
 *       { initialKey: "name", initialDir: "asc" }
 *   );
 *
 *   <th {...headerProps("name")}>Nombre</th>
 */
export function useSortableTable(rows, storageKey, accessors, opts = {}) {
    const initial = useMemo(() => {
        if (typeof window === "undefined") {
            return { key: opts.initialKey || null, dir: opts.initialDir || "asc" };
        }
        try {
            const raw = sessionStorage.getItem(`sort:${storageKey}`);
            if (raw) return JSON.parse(raw);
        } catch (_) {
            /* ignore */
        }
        return { key: opts.initialKey || null, dir: opts.initialDir || "asc" };
    }, [storageKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const [sortKey, setSortKey] = useState(initial.key);
    const [sortDir, setSortDir] = useState(initial.dir);

    const toggle = useCallback(
        (key) => {
            let newKey = key;
            let newDir = "asc";
            if (sortKey === key) {
                newDir = sortDir === "asc" ? "desc" : "asc";
            }
            setSortKey(newKey);
            setSortDir(newDir);
            try {
                sessionStorage.setItem(
                    `sort:${storageKey}`,
                    JSON.stringify({ key: newKey, dir: newDir }),
                );
            } catch (_) {
                /* ignore */
            }
        },
        [sortKey, sortDir, storageKey],
    );

    const sorted = useMemo(() => {
        if (!sortKey || !accessors[sortKey]) return rows;
        const getter = accessors[sortKey];
        const arr = [...rows];
        arr.sort((a, b) => {
            const va = getter(a);
            const vb = getter(b);
            if (va == null && vb == null) return 0;
            if (va == null) return 1;
            if (vb == null) return -1;
            if (typeof va === "number" && typeof vb === "number") {
                return va - vb;
            }
            return String(va).localeCompare(String(vb), "es", {
                numeric: true,
                sensitivity: "base",
            });
        });
        return sortDir === "desc" ? arr.reverse() : arr;
    }, [rows, sortKey, sortDir, accessors]);

    const headerProps = useCallback(
        (key, extraClass = "") => ({
            onClick: () => toggle(key),
            className: `cursor-pointer select-none group ${extraClass}`,
            "data-sort-key": key,
            "data-sort-dir": sortKey === key ? sortDir : "none",
        }),
        [toggle, sortKey, sortDir],
    );

    return { sorted, sortKey, sortDir, toggle, headerProps };
}

export function SortIndicator({ active, dir }) {
    if (!active) {
        return (
            <CaretUpDown
                size={10}
                className="inline-block ml-1 text-slate-300 group-hover:text-slate-500"
            />
        );
    }
    return dir === "asc" ? (
        <CaretUp size={10} className="inline-block ml-1 text-[#002BAF]" />
    ) : (
        <CaretDown size={10} className="inline-block ml-1 text-[#002BAF]" />
    );
}
